import React from 'react';
import { Check, Sparkles } from 'lucide-react';

const motivationalMessages = [
  "You're so close! Your brain is already celebrating! 🎉",
  "Almost there! Feel that dopamine rush coming! ⚡️",
  "One more push! Your neurons are firing with excitement! 🧠",
  "The finish line is in sight! Your mind is ready! 💫",
  "Nearly there! Your brain's reward system is warming up! 🌟",
  "So close to victory! Your synapses are tingling! ✨",
  "Almost achieved! Your neural pathways are strengthening! 🔥",
  "Final stretch! Your brain's motivation centers are lit! 💪"
];

interface GoalProgressProps {
  icon: React.ReactNode;
  name: string;
  target: number;
  completed: number;
  totalDays: number;
  unit?: string;
}

export function GoalProgress({ 
  icon, 
  name, 
  target, 
  completed, 
  totalDays,
  unit = 'days'
}: GoalProgressProps) {
  const progress = (completed / target) * 100;
  const isNearlyComplete = progress >= 80 && progress < 100;
  
  // Get random motivational message
  const [message] = React.useState(() => 
    motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium">{name}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {completed}/{target} {unit}
          </span>
          {completed >= target && (
            <div className="bg-success/20 text-success p-1 rounded-full">
              <Check className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${
              completed >= target ? 'bg-success' : 'bg-primary'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>

        {isNearlyComplete && (
          <div className="flex items-center gap-2 text-xs text-primary animate-pulse">
            <Sparkles className="w-3 h-3" />
            <span>{message}</span>
          </div>
        )}
      </div>
    </div>
  );
}